import { Col, Modal, Typography, Card, Row, Button, Alert, Image, message, Form, Tooltip, Switch, Skeleton, Segmented, Tag } from "antd";
import { FC, useState, useEffect, useCallback, memo, useMemo } from "react";
import { apiErrorMessage, handleLinkedStockDependentsError } from "components/ManageProduct/LinkedStockDependentsModal";
import ProductSKUCreate from "./ProductSKUCreate";
import { LOCAL_STOCK_REF_PREFIX } from "./ProductSKUCreate/SkuModalLinkedStockTab";
import { normalizeLinkedQuantity } from "services/interface/productLinkedStock.interface";
import { SkuModalStockEditProvider, useSkuModalStockEdit } from "./ProductSKUCreate/SkuModalStockEditContext";
import SkuModalStockEditShell from "./ProductSKUCreate/SkuModalStockEditShell";
import { SKU_MODAL_BODY_HEIGHT } from "./ProductSKUCreate/skuModalConstants";
import { getSkuModalTitle, SkuDraggableModalTitle } from "./ProductSKUCreate/skuModalShared";
import { CopyOutlined, DeleteOutlined, EditOutlined } from "@ant-design/icons";
import { backendUrl } from "config";
import { useAppSelector } from "store"; // ✅ เพิ่ม import useAppSelector
import { numberFormat, numberFormatInt } from "utils/nuberFormat";
import { useGetFactoryById } from "hooks/useFactory";
import { useDeleteSku } from "hooks/useProduct";

import type { UploadFile } from "antd/es/upload/interface";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { getSelfShippingBrands } from "services/ordersApi";
import styled from "@emotion/styled";
import { useGetAllCategory } from "hooks/useCategory";
import { findCategoryById } from "./ProductSKUCreate/helpers";
import { IProductStockInfo } from "@/services/interface";
import ProductInventoryGrid from "../ProductInventoryGrid";


// ============================================================
// ✅ Hoisted static styles — ป้องกันสร้าง object ใหม่ทุกรอบ render
// (rendering-hoist-jsx, rerender-memo-with-default-value)
// ============================================================
const CARD_MAIN_STYLE: React.CSSProperties = { width: "100%", padding: 15, borderRadius: 8 };

const SKU_CARD_STYLE: React.CSSProperties = {
  width: "100%",
  padding: 15,
  marginTop: 15,
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
  transition: "box-shadow 0.3s ease",
  position: "relative",
};

const SKU_IMAGE_STYLE: React.CSSProperties = {
  width: "100%",
  height: "100%",
  aspectRatio: "1/1",
  objectFit: "cover",
  borderRadius: "6px",
};

const NO_IMAGE_STYLE: React.CSSProperties = {
  width: "100%",
  aspectRatio: "1/1",
  backgroundColor: "#f5f5f5",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  border: "1px dashed #d9d9d9",
  borderRadius: "6px",
};

const NO_IMAGE_TEXT_STYLE: React.CSSProperties = { color: "#999" };

// ยอดคงเหลือใต้ปุ่มเปิด/ปิดการขาย — สไตล์มินิมอล (ข้อความล้วน)
const STOCK_BALANCE_STYLE: React.CSSProperties = {
  marginTop: 6,
  textAlign: "center",
  fontSize: 12,
  color: "#389e0d",
};

const STOCK_BALANCE_EMPTY_STYLE: React.CSSProperties = {
  marginTop: 6,
  textAlign: "center",
  fontSize: 12,
  color: "#cf1322",
};

const STOCK_BALANCE_DISABLED_STYLE: React.CSSProperties = {
  marginTop: 6,
  textAlign: "center",
  fontSize: 12,
  color: "#bfbfbf",
};

const STATUS_COL_STYLE: React.CSSProperties = {
  display: "flex",
  justifyContent: "center",
  marginTop: 10,
};

const ACTION_BAR_STYLE: React.CSSProperties = {
  marginTop: 10,
  backgroundColor: "#efefef",
  padding: 10,
  display: "flex",
  gap: 8,
};

const ACTION_BUTTON_STYLE: React.CSSProperties = { flex: 1 };

const FLEX_END_STYLE: React.CSSProperties = { display: "flex", justifyContent: "flex-end" };

/** พื้นที่ซ้าย/ขวาในแท็บปรับสต็อก–ประวัติ — เลื่อนแยกกัน */
const STOCK_MODAL_SPLIT_COL_SCROLL: React.CSSProperties = {
  maxHeight: "min(58vh, 560px)",
  overflowY: "auto",
  paddingRight: 8,
  paddingBottom: 4,
};

/** หัวข้อย่อยใน modal จัดการสต็อก (จัดเลย์เอาต์คลาย ๆ กับ modal กำหนดราคา) */
const STOCK_MODAL_SECTION_HEAD: React.CSSProperties = {
  marginBottom: 12,
  paddingBottom: 8,
  borderBottom: "1px solid #f0f0f0",
};

const MARGIN_TOP_15: React.CSSProperties = { marginTop: 15 };

const MARGIN_BOTTOM_5: React.CSSProperties = { marginBottom: 5 };

const REQUIRED_STAR_STYLE: React.CSSProperties = { color: "red" };

const MODAL_STYLE: React.CSSProperties = { top: 20 };

const IMAGE_FALLBACK =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik1RnG4W+FgYxN";


// ============================================================
// ✅ Hoisted static JSX สำหรับ empty state (rendering-hoist-jsx)
// ============================================================
const emptyStateAlert = (
  <Row style={MARGIN_TOP_15}>
    <Col span={23}>
      <Alert
        showIcon
        message="ยังไม่มีรายการสินค้า (ตัวเลือกสินค้า) "
        description="สามารถสร้างสินค้าได้โดยไม่ต้องมี ตัวเลือกสินค้า — หากต้องการเปิดการขาย กรุณาเพิ่มและตั้งค่า ตัวเลือกสินค้า อย่างน้อย 1 รายการ"
        type="info"
      />
    </Col>
  </Row>
);


// ============================================================
// SKUImage Component — แสดงรูปภาพ SKU (แยกออกเป็น memo component)
// ============================================================
interface SKUImageProps {
  imageId?: string;
}

const SKUImage = memo<SKUImageProps>(function SKUImage({ imageId }) {
  // รูปภาพ SKU - แสดงขนาด 1:1
  if (imageId) {
    return (
      <Image
        alt="SKU"
        src={`${backendUrl}images/${imageId}`}
        style={SKU_IMAGE_STYLE}
        fallback={IMAGE_FALLBACK}
      />
    );
  }

  return (
    <div style={NO_IMAGE_STYLE}>
      <span style={NO_IMAGE_TEXT_STYLE}>ไม่มีรูปภาพ</span>
    </div>
  );
});


// ============================================================
// SKUStockBalance Component — แสดงยอดคงเหลือใต้รูป SKU
// ============================================================
interface SKUStockBalanceProps {
  isStock?: boolean;
  stockBalance?: number;
}

const SKUStockBalance = memo<SKUStockBalanceProps>(function SKUStockBalance({ isStock, stockBalance }) {
  // SKU ที่ไม่ได้เปิดระบบจัดการสต็อก — ไม่มียอดคงเหลือให้แสดง
  if (!isStock) {
    return <div style={STOCK_BALANCE_DISABLED_STYLE}>ไม่ได้จัดการสต็อก</div>;
  }

  // ไม่มีสินค้าคงเหลือ — แสดงเป็น "สินค้าหมด" แทนยอด 0
  const balance = stockBalance ?? 0;
  if (balance <= 0) {
    return <div style={STOCK_BALANCE_EMPTY_STYLE}>สินค้าหมด</div>;
  }

  return (
    <div style={STOCK_BALANCE_STYLE}>
      คงเหลือ {numberFormatInt(balance)} ชิ้น
    </div>
  );
});


// ============================================================
// SKUStatus Component — แสดงสถานะการขาย (แยกออกเป็น memo component)
// ============================================================
interface SKUStatusProps {
  isDraft?: boolean;
  isActive?: boolean;
}

const SKUStatus = memo<SKUStatusProps>(function SKUStatus({ isDraft, isActive }) {
  // แสดงสถานะการขาย
  if (isDraft) {
    return <Alert type="warning" message="ร่าง" />;
  }

  if (isActive !== undefined) {
    return isActive
      ? <Alert type="success" message="เปิดการขาย" />
      : <Alert type="error" message="ปิดการขาย" />;
  }

  return <Alert type="warning" message="ไม่ระบุ" />;
});


// ============================================================
// SKUDetailRow Component — แสดงข้อมูลแต่ละแถว (แยกออกเป็น memo component)
// ============================================================
interface SKUDetailRowProps {
  label: string;
  value: React.ReactNode;
}

const SKUDetailRow = memo<SKUDetailRowProps>(function SKUDetailRow({ label, value }) {
  return (
    <Row style={MARGIN_BOTTOM_5}>
      <Col flex="215px">{label}</Col>
      <Col flex="auto">{value}</Col>
    </Row>
  );
});


// ============================================================
// SKUPriceDetails Component — แสดงข้อมูลรายละเอียดราคา (แยกออกเป็น memo component)
// ============================================================
interface SKUPriceDetailsProps {
  firstItem: any;
  price?: Price;
  sellThroughLine: boolean;
}

const SKUPriceDetails = memo<SKUPriceDetailsProps>(function SKUPriceDetails({
  firstItem,
  price,
  sellThroughLine,
}) {
  const { data: selfCarriersData } = useQuery({
    queryKey: ["selfShippingCarriers"],
    queryFn: getSelfShippingBrands,
    staleTime: 5 * 60 * 1000,
  });
  const selfCarriers: { id: number; description: string; image: string }[] = selfCarriersData?.data ?? [];
  const selfCarrier = useMemo(
    () => selfCarriers.find((c) => c.id === price?.self_transport_carrier_id) ?? null,
    [selfCarriers, price?.self_transport_carrier_id]
  );

  return (
    <Col span={17}>
      {/* แสดงข้อมูลรายละเอียด SKU */}
      <SKUDetailRow label="รหัสสินค้า :" value={firstItem?.product_code || "-"} />
      <SKUDetailRow label="ชื่อตัวเลือกสินค้า :" value={firstItem?.name || "-"} />
      <SKUDetailRow
        label="ราคาขาย :"
        value={
          price?.factory_price !== undefined
            ? `${numberFormat(price.factory_price ?? 0)} บาท`
            : `${numberFormat(0)} บาท`
        }
      />
      <SKUDetailRow
        label="ราคาขายแนะนำที่แสดง :"
        value={
          price?.retail_price !== undefined
            ? `${numberFormat(price.retail_price ?? 0)} บาท`
            : `${numberFormat(0)} บาท`
        }
      />
      {/* ✅ แสดงราคา VIP และ wholesale เฉพาะเมื่อ sell_through_line = true */}
      {sellThroughLine ? (
        <>
          <SKUDetailRow
            label="ราคา VIP :"
            value={
              price?.vip_price !== undefined
                ? `${numberFormat(price.vip_price ?? 0)} บาท`
                : `${numberFormat(0)} บาท`
            }
          />
          <SKUDetailRow
            label="ราคาที่ขายใน Line :"
            value={
              price?.wholesale_price !== undefined
                ? `${numberFormat(price.wholesale_price ?? 0)} บาท`
                : `${numberFormat(0)} บาท`
            }
          />
        </>
      ) : null}
      <SKUDetailRow
        label="ขนาดพัสดุ (กว้าง x ยาว x สูง) :"
        value={`${firstItem?.width ?? 0} x ${firstItem?.length ?? 0} x ${firstItem?.height ?? 0} (ซ.ม.)`}
      />
      <SKUDetailRow
        label="น้ำหนัก :"
        value={firstItem?.weight !== undefined ? `${firstItem.weight} กรัม` : "0 กรัม"}
      />
      <SKUDetailRow
        label="การจัดส่ง :"
        value={
          firstItem?.shipping_method === "bangbow"
            ? (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                {firstItem.partner_transport_brand_logo && (
                  <img src={firstItem.partner_transport_brand_logo} alt="transport" style={{ height: 20, maxWidth: 64, objectFit: "contain" }} />
                )}
                {firstItem.partner_transport_brand_name || ""}
              </span>
            )
            : firstItem?.shipping_method === "self"
              ? (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  {selfCarrier?.image && (
                    <img src={selfCarrier.image} alt={selfCarrier.description} style={{ height: 20, maxWidth: 64, objectFit: "contain" }} />
                  )}
                  {selfCarrier ? selfCarrier.description : "ส่งเอง"}
                  {` (ส่งเอง)`}
                </span>
              )
              : "-"
        }
      />
    </Col>
  );
});


// ============================================================
// SKUActions Component — ปุ่มจัดการ คัดลอก / แก้ไข / ลบ
// ============================================================
interface SKUActionsProps {
  variant: Variant;
  price?: Price;
  index: number;
  firstItemId: string;
  firstItemName: string;
  onCopy: (variant: Variant, price: Price) => void;
  onEdit: (variant: Variant, price: Price, index: number) => void;
  onDelete: (variantId: string, itemId: string, skuName: string) => void;
}

const SKUActions = memo<SKUActionsProps>(function SKUActions({
  variant,
  price,
  index,
  firstItemId,
  firstItemName,
  onCopy,
  onEdit,
  onDelete,
}) {
  // ปุ่มคัดลอก - เปิด Modal ในโหมดคัดลอก
  const handleCopyClick = useCallback(() => {
    if (price) onCopy(variant, price);
  }, [price, variant, onCopy]);

  // ปุ่มแก้ไข - เปิด Modal ในโหมดแก้ไข
  const handleEditClick = useCallback(() => {
    if (price) onEdit(variant, price, index);
  }, [price, variant, index, onEdit]);

  // ปุ่มลบ - แสดง modal ยืนยันก่อนลบ SKU
  const handleDeleteClick = useCallback(() => {
    onDelete(variant.id, firstItemId, firstItemName);
  }, [variant.id, firstItemId, firstItemName, onDelete]);

  return (
    <Row>
      <Col span={24} style={ACTION_BAR_STYLE}>
        <Button
          icon={<CopyOutlined />}
          style={ACTION_BUTTON_STYLE}
          onClick={handleCopyClick}
          disabled={!price}
        >
          คัดลอก
        </Button>
        <Button
          icon={<EditOutlined />}
          style={ACTION_BUTTON_STYLE}
          onClick={handleEditClick}
          disabled={!price}
        >
          แก้ไข
        </Button>
        <Button icon={<DeleteOutlined />} style={ACTION_BUTTON_STYLE} onClick={handleDeleteClick}>
          ลบ
        </Button>
      </Col>
    </Row>
  );
});


// ============================================================
// SKUItemCard Component — แสดง SKU แต่ละรายการ
// ✅ แยกออกจาก variants.map เพื่อป้องกัน remount ทุก render
// (rerender-no-inline-components, rerender-memo)
// ============================================================
interface SKUItemCardProps {
  variant: Variant;
  price?: Price;
  index: number;
  sellThroughLine: boolean;
  isEditMode: boolean;
  onCopy: (variant: Variant, price: Price) => void;
  onEdit: (variant: Variant, price: Price, index: number) => void;
  onDelete: (variantId: string, itemId: string, skuName: string) => void;
  /** หน้าเพิ่มสินค้า: ยอดที่คำนวณสดจาก SKU ต้นทาง (แทน stock ที่เก็บไว้ตอนบันทึก) */
  stockBalanceOverride?: number;
}

const SKUItemCard = memo<SKUItemCardProps>(function SKUItemCard({
  variant,
  price,
  index,
  sellThroughLine,
  isEditMode,
  onCopy,
  onEdit,
  onDelete,
  stockBalanceOverride,
}) {
  const firstItem = variant.items && variant.items.length > 0 ? variant.items[0] : null; // ข้อมูล item แรก (สำหรับแสดงขนาดและน้ำหนัก)

  const handleMouseEnter = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    e.currentTarget.style.boxShadow = "0 4px 16px rgba(0, 0, 0, 0.15)";
  }, []);

  const handleMouseLeave = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    e.currentTarget.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.1)";
  }, []);

  return (
    // Card แต่ละตัว - responsive design
    <Col xs={{ span: 24 }} md={{ span: 12 }} lg={{ span: 8 }}>
      <Card
        style={SKU_CARD_STYLE}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {/* มุมขวาบน — ป้าย COD และ ส่งฟรี */}
        {(firstItem?.is_cod || price?.include_transport_price !== false) && (
          <div
            style={{
              position: "absolute",
              top: 8,
              right: 8,
              zIndex: 1,
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            {firstItem?.is_cod && (
              <Tag color="orange" style={{ margin: 0 }}>COD</Tag>
            )}
            {price?.include_transport_price !== false && (
              <Tag color="green" style={{ margin: 0 }}>ส่งฟรี</Tag>
            )}
          </div>
        )}
        {/* ส่วนแสดงข้อมูล SKU */}
        <Row gutter={[16, 16]}>
          {/* ส่วนซ้าย - รูปภาพและสถานะ */}
          <Col span={7}>
            <Row>
              <Col span={24}>
                {/* รูปภาพ SKU - แสดงขนาด 1:1 */}
                <SKUImage imageId={firstItem?.image_id} />
              </Col>
              <Col span={24} style={STATUS_COL_STYLE}>
                {/* แสดงสถานะการขาย */}
                <SKUStatus isDraft={firstItem?.is_draft} isActive={firstItem?.is_active} />
              </Col>
              {/* ✅ ยอดคงเหลือใต้ปุ่มเปิด/ปิดการขาย (หน้าเพิ่มสินค้า = สต็อกเริ่มต้นที่ตั้งไว้ รอบันทึกพร้อมสินค้า) */}
              <Col span={24}>
                <SKUStockBalance
                  isStock={firstItem?.is_stock}
                  stockBalance={stockBalanceOverride ?? firstItem?.stock?.stock_balance}
                />
              </Col>
            </Row>
          </Col>
          {/* ส่วนขวา - ข้อมูลรายละเอียด */}
          <SKUPriceDetails
            firstItem={firstItem}
            price={price}
            sellThroughLine={sellThroughLine}
          />
        </Row>
        {/* ส่วนปุ่มจัดการ */}
        <SKUActions
          variant={variant}
          price={price}
          index={index}
          firstItemId={firstItem?.id || ""}
          firstItemName={firstItem?.name || "SKU นี้"}
          onCopy={onCopy}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      </Card>
    </Col>
  );
});


// ============================================================
// SkuProductModal — Modal SKU (width ตามแท็บ ราคา/คลัง จาก context)
// ============================================================

type SkuProductModalProps = {
  open: boolean;
  onClose: () => void;
  warehouseOnly: boolean;
  setVariants: (variants: VariantsInput) => void;
  variants: VariantsInput;
  setPrices: (prices: PricesInput) => void;
  prices: PricesInput;
  copyMode: boolean;
  editMode: boolean;
  copyData: { variant: Variant; price: Price } | null;
  editData: { variant: Variant; price: Price; index: number } | null;
  skuImage: UploadFile[] | undefined;
  setSkuImage: (skuImage: UploadFile[] | undefined) => void;
  originalPrices: PricesInput;
  originalVariants: VariantsInput;
  factoryId?: string;
  isEditMode: boolean;
  productId?: string;
  percentCommission: number;
  categoryName: string;
  onStockToggled: () => void;
  onSkuCreated?: (variant: Variant, price: Price, index: number) => void;
};

export const SkuProductModal: FC<SkuProductModalProps> = ({
  open,
  onClose,
  warehouseOnly,
  setVariants,
  variants,
  setPrices,
  prices,
  copyMode,
  editMode,
  copyData,
  editData,
  skuImage,
  setSkuImage,
  originalPrices,
  originalVariants,
  factoryId,
  isEditMode,
  productId,
  percentCommission,
  categoryName,
  onStockToggled,
  onSkuCreated,
}) => {
  const { contentWidth } = useSkuModalStockEdit();
  const bodyStyle = useMemo<React.CSSProperties>(
    () => ({
      width: contentWidth,
      maxWidth: "100%",
      height: SKU_MODAL_BODY_HEIGHT,
      overflowY: "auto",
      overflowX: "hidden",
      boxSizing: "border-box",
      transition: "width 0.28s cubic-bezier(0.4, 0, 0.2, 1)",
    }),
    [contentWidth]
  );

  const modalTitle = useMemo(
    () => (
      <SkuDraggableModalTitle>
        {getSkuModalTitle({ editMode, copyMode })}
      </SkuDraggableModalTitle>
    ),
    [editMode, copyMode]
  );

  return (
    <Modal
      title={modalTitle}
      open={open}
      maskClosable={false}
      destroyOnClose
      onCancel={onClose}
      width={contentWidth}
      footer={null}
      style={MODAL_STYLE}
      modalRender={(node) => <SkuModalStockEditShell>{node}</SkuModalStockEditShell>}
    >
      <div style={bodyStyle}>
        <ProductSKUCreate
          setVariants={setVariants}
          variants={variants}
          setPrices={setPrices}
          prices={prices}
          onClose={onClose}
          copyMode={copyMode}
          editMode={editMode}
          copyData={copyData}
          editData={editData}
          skuImage={skuImage}
          setSkuImage={setSkuImage}
          originalPrices={originalPrices}
          originalVariants={originalVariants}
          factoryId={factoryId}
          isEditMode={isEditMode}
          productId={productId}
          percentCommission={percentCommission}
          categoryName={categoryName}
          onStockToggled={onStockToggled}
          warehouseOnly={warehouseOnly}
          onSkuCreated={onSkuCreated}
        />
      </div>
    </Modal>
  );
};

// ============================================================
// ProductSKUCard — Component หลัก
// ============================================================

/**
 * Props สำหรับ ProductSKUCard Component
 * ใช้สำหรับแสดงรายการ SKUs ที่เพิ่มแล้ว และจัดการ SKUs
 */
interface ProductSKUCardProps {
  setVariants: (variants: VariantsInput) => void; // ฟังก์ชันสำหรับอัปเดต variants array
  variants: VariantsInput; // ข้อมูล variants ที่มีอยู่
  setPrices: (prices: PricesInput) => void; // ฟังก์ชันสำหรับอัปเดต prices array
  prices: PricesInput; // ข้อมูล prices ที่มีอยู่
  skuImage: UploadFile[] | undefined;
  setSkuImage: (skuImage: UploadFile[] | undefined) => void;
  isEditMode?: boolean; // โหมดแก้ไขสินค้า (optional)
  setCheckIsActive?: (checkIsActive: boolean) => void; // ฟังก์ชันสำหรับอัปเดต checkIsActive (optional)
  form?: any; // Form instance สำหรับอัปเดต form field (optional)
  originalPrices?: PricesInput; // ✅ เก็บค่าจากฐานข้อมูลเพื่อใช้สำหรับ restore c1-c3 (optional)
  originalVariants?: VariantsInput; // ✅ เก็บค่าจากฐานข้อมูลเพื่อใช้ restore shipping_method + dimensions (optional)
  factoryId?: string; // ✅ รับ factoryId
  productId?: string; // ✅ รับ productId
}

/**
 * ProductSKUCard Component
 * แสดงรายการ SKUs ที่เพิ่มแล้ว และจัดการ SKUs (เพิ่ม/แก้ไข/ลบ/คัดลอก)
 */
const ProductSKUCard: FC<ProductSKUCardProps> = ({
  variants,
  setVariants,
  prices,
  setPrices,
  skuImage,
  setSkuImage,
  isEditMode = false,
  setCheckIsActive,
  form,
  originalPrices = [],
  originalVariants = [],
  factoryId,
  productId,
}) => {
  // ✅ ดึง sell_through_line จาก Redux state
  const { data: factoryData } = useGetFactoryById(factoryId || "");
  // mutate (ไม่ใช่ mutateAsync): error จัดการใน onError แล้ว — mutateAsync จะ reject ต่อเป็น unhandled rejection
  const { mutate: deleteSkuApi } = useDeleteSku();

  // ✅ แก้ไข: ไม่ต้องใช้ useAppSelector เพราะค่ามาจาก factoryData (ไม่ได้ใช้ Redux state)
  const sellThroughLine = factoryData?.factory?.sell_through_line ?? false;
  //const sellThroughLine = useAppSelector((state) => state.auth.profile?.sell_through_line ?? false);

  // State สำหรับควบคุม Modal
  const [open, setOpen] = useState(false); // เปิด/ปิด Modal

  // State สำหรับโหมดการทำงาน
  const [copyMode, setCopyMode] = useState(false); // โหมดคัดลอก
  const [editMode, setEditMode] = useState(false); // โหมดแก้ไข
  const [warehouseOnly, setWarehouseOnly] = useState(false); // เปิด modal มาแต่แสดงเฉพาะ tab คลัง

  // State สำหรับข้อมูลที่ใช้ในโหมดคัดลอกและแก้ไข
  const [copyData, setCopyData] = useState<{ variant: Variant, price: Price } | null>(null); // ข้อมูลสำหรับคัดลอก
  const [editData, setEditData] = useState<{ variant: Variant, price: Price, index: number } | null>(null); // ข้อมูลสำหรับแก้ไข

  const queryClient = useQueryClient();

  const { data: categoryData } = useGetAllCategory();

  // ✅ อ่าน category_id จาก parent form เพื่อหา percent_commission + ชื่อประเภทสินค้า
  const watchedCategoryId = Form.useWatch("category_id", form);

  // ✅ คำนวณ percentCommission และ categoryName จาก categoryData + category_id
  const { percentCommission, categoryName } = useMemo(() => {
    if (!categoryData || !watchedCategoryId) {
      return { percentCommission: 0, categoryName: "" };
    }
    const found = findCategoryById(categoryData, watchedCategoryId);
    return {
      percentCommission: found?.percent_commission ?? 0,
      categoryName: found?.name ?? "",
    };
  }, [categoryData, watchedCategoryId]);


  /**
   * useEffect สำหรับตรวจสอบสถานะ is_active ของ SKU ใน Edit Mode
   * ถ้า SKU ทุกรายการมี is_active = false ให้แจ้งเตือนและปิดการขายสินค้า
   * ถ้ามี SKU ที่เป็น active ให้เปิดการขายสินค้า
   */
  useEffect(() => {
    // ✅ Early return — ข้ามเงื่อนไขที่ไม่ต้องทำงาน (js-early-exit)
    if (!isEditMode || variants.length === 0 || !setCheckIsActive) return;

    // ตรวจสอบว่า SKU ทุกรายการมี is_active = false หรือไม่
    const allSKUsInactive = variants.every(variant =>
      variant.items.every(item => item.is_active === false)
    );

    if (!allSKUsInactive) return;

    // ถ้า SKU ทุกรายการเป็น inactive ให้แจ้งเตือนและปิดการขายสินค้า
    setCheckIsActive(false);
    // ✅ ตั้งค่า form field is_active เป็น false
    if (form) {
      form.setFieldValue("is_active", false);
    }
    message.warning("ระบบได้ปิดการขายสินค้าอัตโนมัติ เนื่องจาก SKU ทุกรายการถูกปิดใช้งาน");
    // ✅ ลบการตั้งค่า is_active อัตโนมัติเมื่อมี SKU ที่เป็น active
    // ให้ผู้ใช้เป็นคนตัดสินใจเองว่าจะเปิดหรือปิดการขายสินค้า
  }, [variants, isEditMode, setCheckIsActive, form]);

  /**
   * ✅ Reset modal state กลับค่าเริ่มต้น (rerender-functional-setstate)
   */
  const resetModalState = useCallback(() => {
    setCopyMode(false);
    setEditMode(false);
    setCopyData(null);
    setEditData(null);
    setWarehouseOnly(false);
  }, []);

  /**
   * เปิด Modal สำหรับเพิ่ม SKU ใหม่
   * รีเซ็ต state ทั้งหมดให้เป็นค่าเริ่มต้น
   */
  const showDrawer = useCallback(() => {
    resetModalState(); // ปิดโหมดคัดลอก + แก้ไข, ล้างข้อมูล
    setOpen(true); // เปิด Modal
  }, [resetModalState]);

  /**
   * ปิด Modal และรีเซ็ต state ทั้งหมด
   */
  const onClose = useCallback(() => {
    setOpen(false); // ปิด Modal
    resetModalState(); // ปิดโหมดคัดลอก + แก้ไข, ล้างข้อมูล
  }, [resetModalState]);

  /**
   * จัดการการคัดลอก SKU
   * เปิด Modal ในโหมดคัดลอกพร้อมข้อมูลที่เลือก
   * @param variant - ข้อมูล variant ที่ต้องการคัดลอก
   * @param price - ข้อมูล price ที่ต้องการคัดลอก
   */
  const handleCopy = useCallback((variant: Variant, price: Price) => {
    setCopyMode(true); // เปิดโหมดคัดลอก
    setEditMode(false); // ปิดโหมดแก้ไข
    setCopyData({ variant, price }); // เก็บข้อมูลสำหรับคัดลอก
    setEditData(null); // ล้างข้อมูลแก้ไข
    setOpen(true); // เปิด Modal
  }, []);

  /**
   * จัดการการแก้ไข SKU
   * เปิด Modal ในโหมดแก้ไขพร้อมข้อมูลที่เลือก
   * @param variant - ข้อมูล variant ที่ต้องการแก้ไข
   * @param price - ข้อมูล price ที่ต้องการแก้ไข
   * @param index - index ของ SKU ที่ต้องการแก้ไข
   */
  const handleEdit = useCallback((variant: Variant, price: Price, index: number) => {
    // ✅ ใช้ข้อมูล price ที่ส่งมาโดยตรง ไม่ต้องใช้ index
    // เพราะ price ที่ส่งมาถูก match ด้วย variant_1_item_id แล้ว
    setEditMode(true); // เปิดโหมดแก้ไข
    setCopyMode(false); // ปิดโหมดคัดลอก
    setWarehouseOnly(false);
    setEditData({
      variant: variant,  // ใช้ข้อมูลที่ส่งมาโดยตรง
      price: price,      // ✅ ใช้ข้อมูลที่ส่งมาโดยตรง (ไม่ใช้ prices[index])
      index
    }); // เก็บข้อมูลสำหรับแก้ไข
    setCopyData(null); // ล้างข้อมูลคัดลอก
    setOpen(true); // เปิด Modal
  }, []);

  /** แก้ไขจากแท็บคลัง (ProductInventoryGrid) — เปิด modal เฉพาะ tab คลัง */
  const handleEditFromInventory = useCallback(
    (skuId: string) => {
      const idx = variants.findIndex((v) => v.items?.[0]?.id === skuId);
      if (idx === -1) {
        message.error("ไม่พบ SKU ที่จะแก้ไข");
        return;
      }
      const variant = variants[idx];
      const price =
        prices.find((p) => p.variant_1_item_id === skuId) ??
        ({ variant_1_item_id: skuId } as Price);

      setEditMode(true);
      setCopyMode(false);
      setWarehouseOnly(true);
      setEditData({ variant, price, index: idx });
      setCopyData(null);
      setOpen(true);
    },
    [variants, prices]
  );

  /**
   * จัดการการลบ SKU
   * แสดง modal ยืนยันก่อนลบ SKU ออกจาก arrays
   * @param variantId - ID ของ variant ที่ต้องการลบ
   * @param itemId - ID ของ item ที่ต้องการลบ (ใช้สำหรับ match กับ prices)
   * @param skuName - ชื่อ SKU ที่ต้องการลบ (สำหรับแสดงใน modal)
   */
  const handleDelete = useCallback((variantId: string, itemId: string, skuName: string) => {
    //refetch product

    Modal.confirm({
      title: 'ยืนยันการลบ SKU',
      content: `คุณต้องการลบ "${skuName}" หรือไม่?`,
      okText: 'ลบ',
      okType: 'danger',
      cancelText: 'ยกเลิก',
      async onOk() {
        // โหมดอัปเดตสินค้า: ลบผ่าน API และ refetch product
        if (isEditMode && itemId) {
          deleteSkuApi(itemId, {
            onSuccess: () => {
              message.success('ลบ SKU สำเร็จ');
              queryClient.refetchQueries({
                queryKey: ['product', productId],
                type: 'all',
              });
            },
            onError: (err: any) => {
              // มีสินค้าอื่นผูกสต็อกของ SKU นี้อยู่ → modal ตารางรายการที่ต้องเคลียร์ก่อน
              if (handleLinkedStockDependentsError(err)) return;
              message.error(apiErrorMessage(err, 'ไม่สามารถลบ SKU ได้'));
            }
          });
        } else {
          // โหมดสร้างสินค้า: ลบเฉพาะใน local state
          const newVariants = variants.filter(v => v.id !== variantId);
          const newPrices = prices.filter(p => p.variant_1_item_id !== itemId);
          setVariants(newVariants);
          setPrices(newPrices);
        }
      },
      onCancel() {
        // ไม่ทำอะไร เมื่อกดยกเลิก
      },
    });
  }, [isEditMode, deleteSkuApi, queryClient, productId, variants, prices, setVariants, setPrices]);

  /** refetch product หลัง toggle stock สำเร็จ */
  const handleStockToggled = useCallback(() => {
    if (productId) {
      queryClient.refetchQueries({ queryKey: ['product', productId], type: 'all' });
    }
  }, [queryClient, productId]);

  // ✅ สร้าง priceMap ด้วย useMemo เพื่อ O(1) lookup แทน .find() ใน loop
  // (js-index-maps, js-set-map-lookups)
  const priceMap = useMemo(() => {
    const map = new Map<string, Price>();
    for (const p of prices) {
      if (p.variant_1_item_id) {
        map.set(p.variant_1_item_id, p);
      }
    }
    return map;
  }, [prices]);

  const hasNoSKU = variants.length === 0 && prices.length === 0;

  /**
   * หน้าเพิ่มสินค้า: ยอดขายได้ของแต่ละ SKU คำนวณสดทุกครั้ง
   * (ขั้นสูงที่ผูก SKU ในสินค้านี้ → ใช้จำนวนเริ่มต้นล่าสุดของ SKU ต้นทาง ไม่ใช่ค่าตอนเลือก)
   */
  const liveStockBalance = useMemo(() => {
    if (isEditMode) return null;
    const items = variants.map((v) => v.items?.[0] as any).filter(Boolean);
    const basicQty = new Map<string, number>(
      items
        .filter((it) => it.is_stock && it.linked_stock?.is_initial_stock_sku)
        .map((it) => [it.id, Number(it.linked_stock?.initial_quantity) || 0])
    );
    const map = new Map<string, number>();
    for (const it of items) {
      if (!it.is_stock) continue;
      const draft = it.linked_stock_draft;
      if (!draft || draft.mode === "basic") {
        map.set(it.id, basicQty.get(it.id) ?? 0);
        continue;
      }
      const rows: any[] = draft.rows ?? [];
      map.set(
        it.id,
        rows.length
          ? Math.min(
              ...rows.map((r) => {
                const bal = String(r.ref_id).startsWith(LOCAL_STOCK_REF_PREFIX)
                  ? basicQty.get(String(r.ref_id).slice(LOCAL_STOCK_REF_PREFIX.length)) ?? 0
                  : Number(r.ref_balance) || 0;
                return Math.floor(bal / normalizeLinkedQuantity(r.quantity));
              })
            )
          : 0
      );
    }
    return map;
  }, [isEditMode, variants]);

  const [activeSkuTab, setActiveSkuTab] = useState<'ตัวเลือกสินค้า' | 'สต๊อกสินค้า'>('ตัวเลือกสินค้า');

  return (
    <WrapperAll>
      {/* Card หลักสำหรับแสดงรายการ SKUs */}
      <Card style={CARD_MAIN_STYLE}>
        {/* Header — Segmented tabs แทนชื่อส่วน */}
        <Row align="middle" style={{ width: '100%' }}>
          <Col flex="auto">
            <Segmented
              value={activeSkuTab}
              onChange={(val) => setActiveSkuTab(val as 'ตัวเลือกสินค้า' | 'สต๊อกสินค้า')}
              options={[
                { label: 'ตัวเลือกสินค้า', value: 'ตัวเลือกสินค้า' },
                { label: 'สต๊อกสินค้า', value: 'สต๊อกสินค้า' },
              ]}
              size="middle"
            />
          </Col>
          {/* ปุ่มเพิ่มรายการ — เฉพาะแท็บสินค้า */}
          {activeSkuTab === 'ตัวเลือกสินค้า' && (
            <Col style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Tooltip title={!watchedCategoryId ? "กรุณาเลือกหมวดหมู่สินค้าก่อน" : undefined}>
                <Button type="primary" onClick={showDrawer} disabled={!watchedCategoryId}>
                  เพิ่มตัวเลือกสินค้า
                </Button>
              </Tooltip>
            </Col>
          )}
        </Row>

        {/* ===== Tab: สินค้า ===== */}
        {activeSkuTab === 'ตัวเลือกสินค้า' && (
          hasNoSKU ? emptyStateAlert : (
            <Row gutter={[10, 16]} style={MARGIN_TOP_15}>
              {variants.map((variant, index) => {
                const firstItem = variant.items && variant.items.length > 0 ? variant.items[0] : null;
                const price = firstItem ? priceMap.get(firstItem.id) : undefined;
                return (
                  <SKUItemCard
                    // หน้าแก้ไข: ทุก SKU ใช้ variant.id เดียวกัน (variant จริงใน DB มีตัวเดียว) → ใช้ id ของ SKU เป็น key
                    key={variant.items?.[0]?.id ?? variant.id}
                    variant={variant}
                    price={price}
                    index={index}
                    sellThroughLine={sellThroughLine}
                    isEditMode={isEditMode}
                    onCopy={handleCopy}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    stockBalanceOverride={firstItem ? liveStockBalance?.get(firstItem.id) : undefined}
                  />
                );
              })}
            </Row>
          )
        )}

        {/* ===== Tab: คลัง ===== */}
        {activeSkuTab === 'สต๊อกสินค้า' && (
          <div style={{ marginTop: 16 }}>
            {isEditMode ? (
              <ProductInventoryGrid
                variants={variants}
                productId={productId}
                onEditSku={handleEditFromInventory}
              />
            ) : (
              // หน้าเพิ่มสินค้า: ตาราง/ปุ่มปรับสต็อกต้องใช้ SKU ที่อยู่ใน DB แล้ว → ตั้งผ่าน modal ของแต่ละ SKU แทน
              <Alert
                type="info"
                showIcon
                message="ตั้งค่าสต็อกได้ที่ปุ่ม “แก้ไข” ของแต่ละตัวเลือกสินค้า → แท็บ “สต็อกสินค้า”"
                description="สต็อกและจำนวนเริ่มต้นจะถูกบันทึกพร้อมกับการกด “สร้างสินค้า”"
              />
            )}
          </div>
        )}
      </Card>

      {/* Modal สำหรับจัดการ SKU */}
      <SkuModalStockEditProvider
        productId={productId}
        open={open}
        initialLayoutTab={warehouseOnly ? "warehouse" : "price"}
      >
        <SkuProductModal
          open={open}
          onClose={onClose}
          warehouseOnly={warehouseOnly}
          setVariants={setVariants}
          variants={variants}
          setPrices={setPrices}
          prices={prices}
          copyMode={copyMode}
          editMode={editMode}
          copyData={copyData}
          editData={editData}
          skuImage={skuImage}
          setSkuImage={setSkuImage}
          originalPrices={originalPrices}
          originalVariants={originalVariants}
          factoryId={factoryId}
          isEditMode={isEditMode}
          productId={productId}
          percentCommission={percentCommission}
          categoryName={categoryName}
          onStockToggled={handleStockToggled}
          onSkuCreated={handleEdit}
        />
      </SkuModalStockEditProvider>
    </WrapperAll>
  );
};

export default ProductSKUCard;

const WrapperAll = styled.div`
  display: flex;
  flex-direction: row;
  align-items: start;
  justify-content: flex-start;
  width: 100%;
  height: 100%;
  background-color: #ffffff;
  border-radius: 8px;
  margin-bottom: 10px;
  margin-top: 10px;
`;

const FormWrapper = styled.div`
  width: 100%;
  height: 100%;
  background-color: #ffffff;
  padding: 20px 20px 0px 20px;
  border-radius: 8px;
`;

